import {createContext, useCallback, useContext, useRef, useState} from 'react'
import type {ReactNode} from 'react'

interface AnnouncerContextValue {
    announce: (message: string) => void
}

const AnnouncerContext = createContext<AnnouncerContextValue | null>(null)

export function AnnouncerProvider({children}: {children: ReactNode}) {
    const [message, setMessage] = useState('')
    const toggleRef = useRef(false)

    const announce = useCallback((next: string) => {
        toggleRef.current = !toggleRef.current
        // WHY: leading zero-width space alternates between two textually-distinct
        // messages so screen readers re-announce identical content without
        // surfacing a visible or audible counter (issue 1077).
        // eslint-disable-next-line i18next/no-literal-string -- zero-width space is structural, not user-visible text.
        setMessage(toggleRef.current ? `\u200B${next}` : next)
    }, [])

    const value: AnnouncerContextValue = {announce}

    return (
        <AnnouncerContext.Provider value={value}>
            {children}
            <div
                role="status"
                aria-live="polite"
                aria-atomic="true"
                className="sr-only"
                data-testid="announcer-live-region"
            >
                {message}
            </div>
        </AnnouncerContext.Provider>
    )
}

export function useAnnouncer(): AnnouncerContextValue {
    const ctx = useContext(AnnouncerContext)
    if (!ctx) {
        return {announce: () => {}}
    }
    return ctx
}
